var _a;
import { Value } from 'typebox/value';
import { getLocalTimeZone, isRecord, registerTypeBoxShortIsoCalendarDateFormat } from '@tadpoles/common';
import snakecaseKeys from 'snakecase-keys';
import { APIError, ApiRequestError, ApiResponseError, ApiResponseParseError, ApiTransportError, UnauthorizedApiRequestError } from './api-error.js';
import { normalizeKeys, stripNullObjectProperties } from './normalize-keys.js';
registerTypeBoxShortIsoCalendarDateFormat();
const ApiClientInfoHeaderNames = {
    appBuildCode: 'X-TADPOLES-APP-BUILD-CODE',
    appId: 'X-TADPOLES-APP-ID',
    appName: 'X-TADPOLES-APP-NAME',
    appVersion: 'X-TADPOLES-APP-VERSION',
    deviceId: 'X-TADPOLES-DEVICE-ID',
    deviceModel: 'X-TADPOLES-DEVICE-MODEL',
    deviceName: 'X-TADPOLES-DEVICE-NAME',
    devicePlatformName: 'X-TADPOLES-DEVICE-PLATFORM-NAME',
    devicePlatformVersion: 'X-TADPOLES-DEVICE-PLATFORM-VERSION',
    deviceTime: 'X-TADPOLES-DEVICE-TIME',
    deviceTimezone: 'X-TADPOLES-DEVICE-TIMEZONE',
    namespace: 'X-TADPOLES-NAMESPACE'
};
export class HttpClient {
    #baseUrl;
    #fetchImplementation;
    #getClientInfo;
    #getHeaders;
    #onUnauthorized;
    #requestController = new AbortController();
    constructor(options) {
        this.#baseUrl = _a.#createBaseUrl(options.baseUrl, options.version ?? 2);
        this.#fetchImplementation = options.fetch ?? fetch;
        this.#getClientInfo = options.getClientInfo;
        this.#getHeaders = options.getHeaders;
        this.#onUnauthorized = options.onUnauthorized;
    }
    abortAllRequests() {
        this.#requestController.abort();
        this.#requestController = new AbortController();
    }
    createUrl(path, queryParams) {
        const requestUrl = new URL(_a.#normalizePath(path), this.#baseUrl);
        if (queryParams) {
            for (const [key, value] of Object.entries(snakecaseKeys(queryParams))) {
                requestUrl.searchParams.set(key, value);
            }
        }
        return requestUrl;
    }
    async getBlob(options) {
        const response = await this.#fetchImplementation(options.url, {
            method: 'GET',
            credentials: 'omit',
            signal: this.#getRequestSignal(options.signal)
        });
        if (!response.ok) {
            throw new ApiRequestError({
                message: 'Blob download failed',
                path: new URL(options.url).pathname,
                responseBody: await response.text(),
                status: response.status,
                statusText: response.statusText
            });
        }
        return await response.blob();
    }
    async head(options) {
        return await this.#sendApiRequest({
            ...options,
            cache: 'no-store',
            credentials: 'include',
            method: 'HEAD',
            redirect: 'manual'
        });
    }
    async get(options) {
        return await this.#requestPayload({
            ...options,
            method: 'GET'
        });
    }
    async post(options) {
        return await this.#requestPayload({
            ...options,
            method: 'POST'
        });
    }
    async postForm(options) {
        const { body, bodySchema, ...requestOptions } = options;
        return await this.#requestPayload({
            ...requestOptions,
            formBody: this.#encodeFormBody(Value.Encode(bodySchema, body)),
            method: 'POST'
        });
    }
    async requestEnvelope(options) {
        if (typeof options.body !== 'undefined' && typeof options.bodySchema === 'undefined') {
            throw new Error('POST requests with a body must provide bodySchema');
        }
        let encodedBody;
        const bodySchema = options.bodySchema;
        if (typeof options.body !== 'undefined' && typeof bodySchema !== 'undefined') {
            encodedBody = Value.Encode(bodySchema, options.body);
        }
        const response = await this.#sendApiRequest({
            ...options,
            body: encodedBody
        });
        const responseBody = await response.text();
        try {
            if (responseBody.length === 0) {
                if (!response.ok) {
                    throw new ApiRequestError({
                        message: `API request failed for ${options.path}`,
                        path: options.path,
                        responseBody,
                        status: response.status,
                        statusText: response.statusText
                    });
                }
                return {
                    status: 'ok',
                    payload: this.#decodePayload(options.payloadSchema, undefined),
                    error: undefined
                };
            }
            const parsedResponse = JSON.parse(responseBody);
            if (!isRecord(parsedResponse)) {
                throw new Error('API response must be an object');
            }
            const status = parsedResponse['status'];
            if (status !== 'ok' && status !== 'error') {
                throw new Error('API response must have status ok|error');
            }
            const rawError = parsedResponse['error'];
            if (typeof rawError !== 'undefined' && !Value.Check(APIError, rawError)) {
                throw new Error('API response error must match APIError');
            }
            if (status === 'error') {
                const decodedError = isRecord(rawError) ? Value.Decode(APIError, rawError) : undefined;
                if (decodedError &&
                    options.acceptedErrorCodes &&
                    options.acceptedErrorCodes.includes(decodedError.code)) {
                    return {
                        status: 'error',
                        error: decodedError,
                        payload: Object.hasOwn(parsedResponse, 'payload')
                            ? this.#decodePayload(options.payloadSchema, parsedResponse['payload'])
                            : undefined
                    };
                }
                throw new ApiResponseError({
                    error: decodedError,
                    path: options.path
                });
            }
            if (!response.ok) {
                throw new ApiRequestError({
                    message: `API request failed for ${options.path}`,
                    path: options.path,
                    responseBody,
                    status: response.status,
                    statusText: response.statusText
                });
            }
            if (!Object.hasOwn(parsedResponse, 'payload')) {
                throw new Error('API response must include payload');
            }
            return {
                status: 'ok',
                error: isRecord(rawError) ? Value.Decode(APIError, rawError) : undefined,
                payload: this.#decodePayload(options.payloadSchema, parsedResponse['payload'])
            };
        }
        catch (error) {
            if (error instanceof ApiRequestError || error instanceof ApiResponseError) {
                throw error;
            }
            throw new ApiResponseParseError(options.path, error);
        }
    }
    async sendRaw(options) {
        const requestHeaders = this.#removeUndefinedHeaders(options.headers ?? {});
        const request = {
            method: options.method ?? 'PUT',
            headers: requestHeaders,
            body: options.body
        };
        const signal = this.#getRequestSignal(options.signal);
        const response = await this.#fetchImplementation(options.url, typeof signal === 'undefined' ? request : { ...request, signal });
        if (!response.ok) {
            throw new ApiRequestError({
                message: `Upload request failed for ${options.url}`,
                path: options.url,
                responseBody: await response.text(),
                status: response.status,
                statusText: response.statusText
            });
        }
    }
    async #sendApiRequest(options) {
        const requestUrl = this.createUrl(options.path, options.queryParams);
        const clientInfoHeaders = this.#getClientInfo
            ? _a.#createClientInfoHeaders({
                deviceTime: String(Date.now()),
                deviceTimezone: getLocalTimeZone(),
                ...(await this.#getClientInfo())
            })
            : {};
        const sharedHeaders = this.#getHeaders ? await this.#getHeaders() : {};
        let requestBody;
        if (typeof options.body !== 'undefined') {
            requestBody = this.#encodeRequestBody(options.body);
        }
        else if (typeof options.formBody !== 'undefined') {
            requestBody = options.formBody;
        }
        const requestHeaders = this.#mergeHeaders({ Accept: 'application/json' }, clientInfoHeaders, sharedHeaders, options.headers ?? {}, typeof options.body !== 'undefined'
            ? { 'Content-Type': 'application/json' }
            : typeof options.formBody !== 'undefined'
                ? { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
                : {});
        const request = {
            method: options.method ?? 'GET',
            headers: requestHeaders,
            body: requestBody,
            ...(options.cache ? { cache: options.cache } : {}),
            ...(options.credentials ? { credentials: options.credentials } : {}),
            ...(options.redirect ? { redirect: options.redirect } : {})
        };
        const signal = this.#getRequestSignal(options.signal);
        let response;
        try {
            response = await this.#fetchImplementation(requestUrl, typeof signal === 'undefined' ? request : { ...request, signal });
        }
        catch (error) {
            if (signal?.aborted) {
                throw error;
            }
            throw new ApiTransportError(options.path, error);
        }
        if (response.status === 401) {
            const responseBody = await response.clone().text();
            if (this.#onUnauthorized) {
                await this.#onUnauthorized({ path: options.path, response: response.clone() });
            }
            throw new UnauthorizedApiRequestError({
                path: options.path,
                responseBody,
                statusText: response.statusText
            });
        }
        if (!response.ok) {
            const responseBody = await response.clone().text();
            if (response.headers.get('Content-Type')?.includes('application/json')) {
                return response;
            }
            throw new ApiRequestError({
                message: `API request failed for ${options.path}`,
                path: options.path,
                responseBody,
                status: response.status,
                statusText: response.statusText
            });
        }
        return response;
    }
    #getRequestSignal(signal) {
        if (signal) {
            return AbortSignal.any([signal, this.#requestController.signal]);
        }
        return this.#requestController.signal;
    }
    async #requestPayload(options) {
        const parsedResponse = await this.requestEnvelope(options);
        if (parsedResponse.status === 'error') {
            throw new ApiResponseError({
                error: parsedResponse.error,
                path: options.path
            });
        }
        return parsedResponse.payload;
    }
    #removeUndefinedHeaders(headers) {
        const normalizedHeaders = {};
        for (const [key, value] of Object.entries(headers)) {
            if (typeof value === 'string') {
                normalizedHeaders[key] = value;
            }
        }
        return normalizedHeaders;
    }
    #mergeHeaders(...headerLayers) {
        const entriesByLowercaseName = new Map();
        for (const headers of headerLayers) {
            for (const [key, value] of Object.entries(headers)) {
                if (typeof value === 'string') {
                    entriesByLowercaseName.set(key.toLowerCase(), [key, value]);
                }
            }
        }
        return Object.fromEntries(entriesByLowercaseName.values());
    }
    #decodePayload(payloadSchema, payload) {
        return Value.Decode(payloadSchema, stripNullObjectProperties(normalizeKeys(payload)));
    }
    #encodeRequestBody(body) {
        if (isRecord(body)) {
            return JSON.stringify(snakecaseKeys(body, { deep: true }));
        }
        return JSON.stringify(body);
    }
    #encodeFormBody(body) {
        if (!isRecord(body)) {
            throw new Error('Form request bodies must encode to an object');
        }
        const formBody = {};
        for (const [key, value] of Object.entries(body)) {
            if (value !== undefined) {
                formBody[key] = String(value);
            }
        }
        return new URLSearchParams(snakecaseKeys(formBody));
    }
    static #createBaseUrl(baseUrl, version) {
        const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
        return new URL(`remote/v${version}/`, normalizedBaseUrl).toString();
    }
    static #normalizePath(path) {
        return path.startsWith('/') ? path.slice(1) : path;
    }
    static #createClientInfoHeaders(clientInfo) {
        for (const clientInfoName of ['appId', 'appName', 'appVersion']) {
            if (!clientInfo[clientInfoName]?.trim()) {
                throw new Error(`API client metadata requires ${clientInfoName}`);
            }
        }
        const headers = {};
        for (const [clientInfoName, headerName] of Object.entries(ApiClientInfoHeaderNames)) {
            const value = clientInfo[clientInfoName]?.trim();
            if (value) {
                headers[headerName] = value;
            }
        }
        return headers;
    }
}
_a = HttpClient;
